import { useState, useCallback, useEffect, useMemo } from "react";
import { IFile } from "./models";
import {
    Modal,
    Row,
    Col,
    Spinner,
    Alert
} from "react-bootstrap";
import { apiGet } from "./api";
import SelectStatic from "./components/select/SelectStatic";
import "./RepositoryBrowser.scss";

interface ILocalFloatingInputProps {
    name: string;
    label: string;
    value: string;
    onChange: (v: string) => void;
}

// TODO: Заменить инпут на общий из components (после мерджа редизайна политик)
function LocalFloatingInput({ name, label, value, onChange }: ILocalFloatingInputProps) {
    const hasValue = value !== "";
    const rootClasses = [
        "rd-input",
        "rd-input--medium",
        "rd-input--floating",
        hasValue ? "has-value" : "",
    ].filter(Boolean).join(" ");
    return (
        <div className={rootClasses}>
            <div className="rd-input__wrapper">
                <input
                    className="rd-input__field"
                    type="text"
                    name={name}
                    value={value}
                    placeholder=" "
                    autoComplete="off"
                    onChange={e => onChange(e.target.value)}
                />
                <label className="rd-input__label">{label}</label>
                {hasValue && (
                    <button
                        type="button"
                        className="rd-input__clear is-visible"
                        onClick={() => onChange("")}
                        tabIndex={-1}
                        aria-label="Clear"
                    >
                        <i className="rd-icon-cross" />
                    </button>
                )}
            </div>
        </div>
    );
}

interface IFileResponse {
    count: number;
    items: IFile[];
}

interface IFileRequest {
    offset: number;
    limit: number;
    search: string;
    ordering?: string;
    repository_id?: number;
}

interface IRepository {
    id: number;
    name: string;
}

export interface IRepositoryBrowserProps {
    onSelect: (file: IFile | null) => void;
}

export const RepositoryBrowser = ({ onSelect }: IRepositoryBrowserProps) => {
    // -------- State
    const [request, setRequest] = useState<IFileRequest>({
        offset: 0,
        limit: 50,
        search: "",
        ordering: ""
    });
    const [items, setItems] = useState<IFile[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [selectedIndex, setSelectedIndex] = useState<number>(-1);

    const [repositories, setRepositories] = useState<IRepository[]>([]);
    const [selectedRepositoryId, setSelectedRepositoryId] = useState<number | null>(null);

    // -------- Initial repositories + URL param
    useEffect(() => {
        const loadRepositories = async () => {
            try {
                const data = await apiGet<IRepository[]>("/api/repositories");
                setRepositories(data);
            } catch (err) {
                console.error("Failed to load repositories:", err);
            }
        };
        loadRepositories();

        const urlParams = new URLSearchParams(window.location.search);
        const repositoryIdParam = urlParams.get("repository_id");
        if (repositoryIdParam) {
            const repositoryId = parseInt(repositoryIdParam, 10);
            if (!isNaN(repositoryId)) {
                setSelectedRepositoryId(repositoryId);
                setRequest(prev => ({ ...prev, repository_id: repositoryId, offset: 0 }));
            }
        }
    }, []);

    // -------- Data loading
    const fetchPage = useCallback(
        async (nextReq: IFileRequest, append: boolean) => {
            setLoading(true);
            setError(null);

            const params: Record<string, string> = {
                offset: String(nextReq.offset),
                limit: String(nextReq.limit)
            };
            if (nextReq.search) params.search = nextReq.search;
            if (nextReq.ordering) params.ordering = nextReq.ordering;
            if (nextReq.repository_id)
                params.repository_id = String(nextReq.repository_id);

            try {
                const data = await apiGet<IFileResponse>(
                    "/api/repository/list/",
                    params
                );
                setTotal(data.count);
                setItems(prev =>
                    append ? [...prev, ...data.items] : data.items
                );
            } catch (err) {
                console.error("Failed to load files:", err);
                if (!append) {
                    setItems([]);
                    setTotal(0);
                }
                setError("Не удалось загрузить данные. Попробуйте ещё раз.");
            } finally {
                setLoading(false);
            }
        },
        []
    );

    useEffect(() => {
        fetchPage(request, false);
        setSelectedIndex(-1);
        onSelect(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request.search, request.ordering, request.repository_id]);

    const select = useCallback(
        (index: number) => {
            setSelectedIndex(index);
            onSelect(items[index] ?? null);
        },
        [items, onSelect]
    );

    const handleSearchChange = useCallback((query: string) => {
        setRequest(prev => ({ ...prev, search: query, offset: 0 }));
    }, []);

    const handleRepositoryChange = useCallback((value: string) => {
        const repositoryId = value ? parseInt(value, 10) : null;
        setSelectedRepositoryId(repositoryId);

        setRequest(prev => ({
            ...prev,
            repository_id: repositoryId ?? undefined,
            offset: 0
        }));

        const urlParams = new URLSearchParams(window.location.search);
        if (repositoryId)
            urlParams.set("repository_id", String(repositoryId));
        else urlParams.delete("repository_id");
        const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
        window.history.replaceState({}, "", newUrl);
    }, []);

    const handleSort = useCallback((dataKey: string) => {
        setRequest(prev => {
            let newOrdering: string;
            if (prev.ordering === dataKey) newOrdering = `-${dataKey}`;
            else if (prev.ordering === `-${dataKey}`) newOrdering = "";
            else newOrdering = dataKey;
            return { ...prev, ordering: newOrdering, offset: 0 };
        });
    }, []);

    const canLoadMore = useMemo(
        () => items.length < total,
        [items.length, total]
    );

    const loadMore = useCallback(() => {
        if (loading || !canLoadMore) return;
        const nextReq = {
            ...request,
            offset: items.length,
            limit: 50
        };
        fetchPage(nextReq, true);
    }, [loading, canLoadMore, request, items.length, fetchPage]);

    type SortDir = "asc" | "desc" | null;

    const orderingState: Record<
        "id" | "name" | "created" | "repository_name",
        SortDir
    > = useMemo(
        () => ({
            id:
                request.ordering === "id"
                    ? "asc"
                    : request.ordering === "-id"
                        ? "desc"
                        : null,
            name:
                request.ordering === "original_filename"
                    ? "asc"
                    : request.ordering === "-original_filename"
                        ? "desc"
                        : null,
            created:
                request.ordering === "created"
                    ? "asc"
                    : request.ordering === "-created"
                        ? "desc"
                        : null,
            repository_name:
                request.ordering === "repository__name"
                    ? "asc"
                    : request.ordering === "-repository__name"
                        ? "desc"
                        : null
        }),
        [request.ordering]
    );

    const sortIcon = (state: SortDir) => {
        if (state === "asc") {
            return <i className="fas fa-sort-up ms-1 text-muted"></i>;
        }
        if (state === "desc") {
            return <i className="fas fa-sort-down ms-1 text-muted"></i>;
        }
        return <i className="fas fa-sort ms-1 text-muted opacity-50"></i>;
    };

    const onRowKeyDown = (
        e: React.KeyboardEvent<HTMLTableRowElement>,
        idx: number
    ) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            select(idx);
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            const next = Math.min(
                items.length - 1,
                selectedIndex === -1 ? 0 : selectedIndex + 1
            );
            setSelectedIndex(next);
            onSelect(items[next] ?? null);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            const prev = Math.max(
                0,
                selectedIndex === -1 ? 0 : selectedIndex - 1
            );
            setSelectedIndex(prev);
            onSelect(items[prev] ?? null);
        }
    };

    const mutedStyle = { color: "var(--rd-text-tertiary)" } as React.CSSProperties;

    const repositoryOptions = useMemo(
        () => [
            { value: "", label: "Все репозитории" },
            ...repositories.map(r => ({ value: String(r.id), label: r.name }))
        ],
        [repositories]
    );

    return (
        <div style={{ color: "var(--rd-text-primary)" }}>
            {/* Filters */}
            <Row className="mb-3 gy-2">
                <Col md={6}>
                    <SelectStatic
                        mode="static"
                        name="repository"
                        label="Репозиторий"
                        floating
                        size="medium"
                        clearable
                        options={repositoryOptions}
                        initialValue={selectedRepositoryId !== null ? String(selectedRepositoryId) : ""}
                        onChange={handleRepositoryChange}
                    />
                </Col>
                <Col md={6}>
                    <LocalFloatingInput
                        name="search"
                        label="Поиск"
                        value={request.search}
                        onChange={handleSearchChange}
                    />
                </Col>
            </Row>

            {/* Results info */}
            <div className="mb-2">
                <small style={mutedStyle}>
                    {total > 0
                        ? `Найдено ${total} файл(ов)${selectedRepositoryId
                            ? " в выбранном репозитории"
                            : ""
                        }`
                        : "Файлы не найдены"}
                </small>
            </div>

            {error && (
                <Alert variant="danger" className="py-2">
                    {error}
                </Alert>
            )}

            {/* Styled table */}
            <div
                className="table-responsive"
                style={{ maxHeight: "500px", overflowY: "auto" }}
            >
                <table className="rd-table mb-0 align-middle">
                    <thead>
                        <tr>
                            <th
                                scope="col"
                                role="button"
                                onClick={() => handleSort("id")}
                                className="text-center"
                            >
                                #
                                {sortIcon(orderingState.id)}
                            </th>
                            <th
                                scope="col"
                                role="button"
                                onClick={() =>
                                    handleSort("original_filename")
                                }
                            >
                                Название {sortIcon(orderingState.name)}
                            </th>
                            <th
                                scope="col"
                                role="button"
                                onClick={() => handleSort("created")}
                            >
                                Создан {sortIcon(orderingState.created)}
                            </th>
                            <th
                                scope="col"
                                role="button"
                                onClick={() =>
                                    handleSort("repository__name")
                                }
                            >
                                Репозиторий{" "}
                                {sortIcon(orderingState.repository_name)}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((row, idx) => {
                            const isSelected = idx === selectedIndex;
                            const created = (row as any)?.created as
                                | string
                                | undefined;
                            const repoName = (row as any)
                                ?.repository_name as string | undefined;
                            const displayName =
                                (row as any)?.name ??
                                (row as any)?.original_filename ??
                                "";

                            return (
                                <tr
                                    key={(row as any).id ?? `${idx}`}
                                    onClick={() => select(idx)}
                                    onKeyDown={e =>
                                        onRowKeyDown(e, idx)
                                    }
                                    tabIndex={0}
                                    role="row"
                                    aria-selected={isSelected}
                                    className={
                                        isSelected
                                            ? "rd-repo-browser__row--selected"
                                            : undefined
                                    }
                                    style={{
                                        cursor: "pointer",
                                        transition: "background 0.2s"
                                    }}
                                >
                                    <th
                                        scope="row"
                                        className="text-center"
                                    >
                                        {(row as any).id ?? ""}
                                    </th>
                                    <td>{displayName}</td>
                                    <td>
                                        {created
                                            ? new Date(
                                                created
                                            ).toLocaleString()
                                            : ""}
                                    </td>
                                    <td>{repoName ?? ""}</td>
                                </tr>
                            );
                        })}
                        {loading && (
                            <tr>
                                <td
                                    colSpan={4}
                                    className="text-center py-3"
                                >
                                    <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                    />
                                    Загрузка...
                                </td>
                            </tr>
                        )}
                        {!loading && items.length === 0 && (
                            <tr>
                                <td
                                    colSpan={4}
                                    className="text-center py-3"
                                    style={mutedStyle}
                                >
                                    Нет данных
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Load more */}
            <div className="d-flex justify-content-center align-items-center gap-2 py-3">
                <small style={mutedStyle}>
                    Показано {items.length} из {total}
                </small>
                <button
                    type="button"
                    className="rd-btn rd-btn--small rd-btn--outline rd-btn--primary"
                    onClick={loadMore}
                    disabled={!canLoadMore || loading}
                >
                    {loading ? "Загрузка..." : "Показать ещё"}
                </button>
            </div>
        </div>
    );
};

export interface IRepositoryBrowserModalProps {
    onSelect: (file: IFile) => void;
    onClose: () => void;
}

export function RepositoryBrowserModal({
    onClose,
    onSelect
}: IRepositoryBrowserModalProps) {
    const [file, setFile] = useState<IFile | null>(null);

    return (
        <Modal backdrop="static" onHide={onClose} show={true} size="xl" contentClassName="shadow-card">
            <Modal.Header closeButton>
                <Modal.Title>Файловый репозиторий</Modal.Title>
            </Modal.Header>

            <Modal.Body className="user-select-none">
                <RepositoryBrowser onSelect={f => setFile(f)} />
            </Modal.Body>

            <Modal.Footer>
                <button
                    type="button"
                    className="rd-btn rd-btn--medium rd-btn--outline rd-btn--secondary"
                    onClick={onClose}
                >
                    Закрыть
                </button>
                <button
                    type="button"
                    className="rd-btn rd-btn--medium rd-btn--primary"
                    disabled={!file}
                    onClick={() => onSelect(file!)}
                >
                    Выбрать
                </button>
            </Modal.Footer>
        </Modal>
    );
}
